import React from "react";

import { Box, Button, Img, Text, Tag } from "../../ui";
import { STATUS, ORDER_ITEM_STATUS_MAP } from "../utils";

export function DMOrderItemCard(props) {
    const {
        orderStatus,
        customerDataItem,
        name,
        price,
        areas,
        addresses,
        deliveryDate,
        image,
        status,
        hasFreeCopy,
        additionalCopies,
        handleOrderItemAgain,
        setItemToOrderAgain,
        toggleUploadDatabaseOverlay
    } = props;

    const handleOrderAgain = event => {
        const isUsingCustomDatabase = customerDataItem?.Targeting?.MapData?.FromCSV;

        if (isUsingCustomDatabase) {
            setItemToOrderAgain(customerDataItem);
            toggleUploadDatabaseOverlay(event);
        } else {
            handleOrderItemAgain(customerDataItem);
        }
    };

    const tag = ORDER_ITEM_STATUS_MAP[status];

    const orderItemStatuses = [STATUS.ACKNOWLEDGED, STATUS.SHIPPED];

    const isOrderReceived = orderStatus === STATUS.APPROVED && status === STATUS.ACTIVE;

    return (
        <Box border="1px solid #CFD0CF" borderRadius="5px" padding={["1.6rem", "3.2rem"]} paddingBottom={["2.4rem", "3.2rem"]}>
            <Box flexDirection={["column", "row"]} gap={[0, "5.6rem"]}>
                <Box flexDirection={["column", "row"]} gap={["2.4rem", "3.2rem"]} width="100%">
                    <Box height={["130px", "300px"]} width={["100%", "29rem"]}>
                        <Img borderRadius="5px" objectFit="cover" src={image} alt="order thumbnail" height="100%" width="100%" />
                    </Box>
                    <Box gap="2.4rem" width="100%">
                        <Box borderBottom="1px solid #CFD0CF">
                            <h4>{name}</h4>
                        </Box>
                        <Box gap="2.4rem">
                            <Box flexDirection={["column", "row"]}>
                                <Text width="12rem" fontSize={["14px", "16px"]}>
                                    Delivery areas:
                                </Text>
                                {!!areas && (
                                    <Text fontWeight={700} fontSize={["14px", "16px"]}>
                                        {areas ? `${areas} ${areas === 1 ? "area" : "areas"},` : null} {addresses} {addresses === 1 ? "address" : "addresses"}
                                    </Text>
                                )}
                            </Box>
                            <Box flexDirection={["column", "row"]}>
                                <Text width="12rem" fontSize={["14px", "16px"]}>
                                    Delivery date:
                                </Text>
                                <Box flexDirection="row" justifyContent="space-between" gap="2.4rem">
                                    <Text fontWeight={700} fontSize={["14px", "16px"]}>
                                        {deliveryDate}
                                    </Text>
                                    {orderItemStatuses.includes(status) && !isOrderReceived && (
                                        <Tag backgroundColor={tag.backgroundColor} dotColor={tag.dotColor}>
                                            {tag.label}
                                        </Tag>
                                    )}
                                    {isOrderReceived && (
                                        <Tag backgroundColor={ORDER_ITEM_STATUS_MAP[STATUS.ACTIVE].backgroundColor} dotColor={ORDER_ITEM_STATUS_MAP[STATUS.ACTIVE].dotColor}>
                                            {ORDER_ITEM_STATUS_MAP[STATUS.ACTIVE].label}
                                        </Tag>
                                    )}
                                </Box>
                            </Box>
                            <Box flexDirection={["column", "row"]} alignItems={["flex-start", "center"]} gap="2.4rem">
                                <Box flexDirection="row" alignItems="center" background="#F3F3F3" height={["48px", "66px"]} padding="0rem 3rem" borderRadius="5px">
                                    <Text as="span" fontSize={["18px", "20px"]} fontWeight={700}>
                                        {price}
                                    </Text>
                                </Box>
                                <Box flexDirection="row" alignItems="center" gap="0.8rem">
                                    <Text as="span" fontSize="12px">
                                        Includes: printing
                                    </Text>
                                    <PrinterIcon />
                                    <Text as="span" fontSize="12px">
                                        + delivery{" "}
                                    </Text>
                                    <DeliveryIcon />
                                </Box>
                            </Box>
                            {(hasFreeCopy || Object.values(additionalCopies).length > 0) && (
                                <div>
                                    {hasFreeCopy && (
                                        <Box flexDirection="row" alignItems="center" gap="0.8rem" paddingBlock="1.6rem" borderTop="1px solid rgba(207, 208, 207, 1)">
                                            <FreeCopyIcon />
                                            <Text as="span" fontSize={["13px", "14px"]} lineHeight={["22px", "24px"]}>
                                                1 additional copy added
                                            </Text>
                                        </Box>
                                    )}
                                    {Object.values(additionalCopies).length > 0 && (
                                        <Box flexDirection="row" alignItems="center" gap="0.8rem" paddingBlock="1.6rem" borderTop="1px solid rgba(207, 208, 207, 1)">
                                            <AdditionalCopiesIcon />
                                            <Text as="span" fontSize={["13px", "14px"]} lineHeight={["22px", "24px"]}>
                                                {`${additionalCopies?.noOfCopies} additional copies added ${additionalCopies?.price}`}
                                            </Text>
                                        </Box>
                                    )}
                                </div>
                            )}
                        </Box>
                    </Box>
                </Box>
                {orderStatus === STATUS.APPROVED && (
                    <Box marginTop={["2.4rem", 0]}>
                        <Button width={["100%", "170px"]} type="button" onClick={handleOrderAgain}>
                            Order again
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function PrinterIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 16C7 15.7929 7.16789 15.625 7.375 15.625H16.625C16.8321 15.625 17 15.7929 17 16C17 16.2071 16.8321 16.375 16.625 16.375H7.375C7.16789 16.375 7 16.2071 7 16ZM7 18C7 17.7929 7.16789 17.625 7.375 17.625H16.625C16.8321 17.625 17 17.7929 17 18C17 18.2071 16.8321 18.375 16.625 18.375H7.375C7.16789 18.375 7 18.2071 7 18ZM7 20C7 19.7929 7.16789 19.625 7.375 19.625H16.625C16.8321 19.625 17 19.7929 17 20C17 20.2071 16.8321 20.375 16.625 20.375H7.375C7.16789 20.375 7 20.2071 7 20Z"
                fill="#003D4C"
            />
            <path
                d="M2.5 11C2.5 10.7929 2.66789 10.625 2.875 10.625H21.125C21.3321 10.625 21.5 10.7929 21.5 11C21.5 11.2071 21.3321 11.375 21.125 11.375H2.875C2.66789 11.375 2.5 11.2071 2.5 11Z"
                fill="#00A76A"
            />
            <path
                d="M3.25 12.75C3.25 12.5429 3.08211 12.375 2.875 12.375C2.66789 12.375 2.5 12.5429 2.5 12.75V14C2.5 14.2071 2.66789 14.375 2.875 14.375C3.08211 14.375 3.25 14.2071 3.25 14V12.75Z"
                fill="#00A76A"
            />
            <path
                d="M21.125 14.375C20.9179 14.375 20.75 14.2071 20.75 14V12.75C20.75 12.5429 20.9179 12.375 21.125 12.375C21.3321 12.375 21.5 12.5429 21.5 12.75V14C21.5 14.2071 21.3321 14.375 21.125 14.375Z"
                fill="#00A76A"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.5 1.5C5.01675 1.5 4.625 1.89175 4.625 2.375V4.5H2C1.79289 4.5 1.625 4.66789 1.625 4.875V7.5H1C0.516751 7.5 0.125 7.89175 0.125 8.375V17.875C0.125 18.3582 0.516751 18.75 1 18.75H4.5V22.375C4.5 22.6511 4.72386 22.875 5 22.875H19C19.2761 22.875 19.5 22.6511 19.5 22.375V18.75H23C23.4832 18.75 23.875 18.3582 23.875 17.875V8.375C23.875 7.89175 23.4832 7.5 23 7.5H22.375V4.875C22.375 4.66789 22.2071 4.5 22 4.5H19.375V2.375C19.375 1.89175 18.9832 1.5 18.5 1.5H5.5ZM1 8.25C0.930964 8.25 0.875 8.30596 0.875 8.375V15.125H4.5V13.375C4.5 13.0989 4.72386 12.875 5 12.875H19C19.2761 12.875 19.5 13.0989 19.5 13.375V15.125H23.125V8.375C23.125 8.30596 23.069 8.25 23 8.25H1ZM2.375 7.5V5.25H4.625V7.5H2.375ZM5.375 7.5H18.625V2.375C18.625 2.30596 18.569 2.25 18.5 2.25H5.5C5.43096 2.25 5.375 2.30596 5.375 2.375V7.5ZM21.625 5.25V7.5H19.375V5.25H21.625ZM19.5 18H23C23.069 18 23.125 17.944 23.125 17.875V15.875H19.5V18ZM0.875 15.875V17.875C0.875 17.944 0.930965 18 1 18H4.5V15.875H0.875ZM18.75 22.125H5.25V13.625H18.75V22.125Z"
                fill="#00A76A"
            />
        </svg>
    );
}
function DeliveryIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M22.3199 10.9389L21.6984 8.4529C21.8683 8.4168 21.9958 8.26605 21.9958 8.08545V7.68462C21.9958 6.81434 21.2878 6.10634 20.4175 6.10634H17.5867V5.27955C17.5867 4.8513 17.2383 4.50293 16.81 4.50293H2.37994C1.95169 4.50293 1.60331 4.8513 1.60331 5.27955V12.0938C1.60331 12.3013 1.77155 12.4696 1.97911 12.4696C2.18663 12.4696 2.35491 12.3013 2.35491 12.0938V5.27955C2.35491 5.26573 2.36611 5.25452 2.37994 5.25452H16.81C16.8238 5.25452 16.835 5.26573 16.835 5.27955V12.0939C16.835 12.3014 17.0032 12.4697 17.2108 12.4697C17.4183 12.4697 17.5866 12.3014 17.5866 12.0939V11.6679H22.0195C22.0199 11.6679 22.0202 11.668 22.0205 11.668C22.0209 11.668 22.0212 11.6679 22.0215 11.6679C22.567 11.6683 23.0302 12.0263 23.1893 12.5197H22.0208C21.8133 12.5197 21.645 12.6879 21.645 12.8955V13.6971C21.645 14.3464 22.1732 14.8746 22.8225 14.8746H23.2484V16.528H22.2658C21.943 15.596 21.057 14.9246 20.0166 14.9246C18.9762 14.9246 18.0902 15.596 17.7675 16.528H17.5865V13.6971C17.5865 13.4896 17.4183 13.3213 17.2107 13.3213C17.0032 13.3213 16.8349 13.4895 16.8349 13.6971V16.5279H9.0383C8.71552 15.5959 7.82953 14.9246 6.78914 14.9246C5.74875 14.9246 4.86272 15.5959 4.53998 16.5279H2.37994C2.36611 16.5279 2.35491 16.5167 2.35491 16.5029V15.6761H3.9833C4.19081 15.6761 4.35909 15.5079 4.35909 15.3004C4.35909 15.0928 4.19086 14.9246 3.9833 14.9246H0.375797C0.168281 14.9246 0 15.0928 0 15.3004C0 15.5079 0.168234 15.6761 0.375797 15.6761H1.60336V16.5029C1.60336 16.9311 1.95173 17.2795 2.37998 17.2795H4.40986C4.40977 17.2879 4.4092 17.2961 4.4092 17.3045C4.4092 18.6169 5.47688 19.6845 6.78914 19.6845C8.10141 19.6845 9.16908 18.6169 9.16908 17.3045C9.16908 17.2961 9.16852 17.2879 9.16842 17.2795H17.6373C17.6372 17.2879 17.6367 17.2961 17.6367 17.3045C17.6367 18.6169 18.7043 19.6845 20.0166 19.6845C21.3289 19.6845 22.3965 18.6169 22.3965 17.3045C22.3965 17.2961 22.396 17.2879 22.3959 17.2795H23.6242C23.8317 17.2795 24 17.1113 24 16.9037V12.8954C24 11.9058 23.2698 11.0835 22.3199 10.9389ZM17.5867 6.85788H20.4175C20.8734 6.85788 21.2443 7.22876 21.2443 7.68462V7.70965H17.5867V6.85788ZM17.5867 10.9163V8.4612H20.9258L21.5396 10.9163H17.5867ZM6.78914 18.933C5.89125 18.933 5.16075 18.2026 5.16075 17.3046C5.16075 16.4067 5.89125 15.6762 6.78914 15.6762C7.68703 15.6762 8.41753 16.4067 8.41753 17.3046C8.41753 18.2026 7.68703 18.933 6.78914 18.933ZM20.0167 18.933C19.1188 18.933 18.3883 18.2026 18.3883 17.3046C18.3883 16.4067 19.1188 15.6762 20.0167 15.6762C20.9146 15.6762 21.6451 16.4067 21.6451 17.3046C21.6451 18.2026 20.9146 18.933 20.0167 18.933ZM23.2485 14.123H22.8225C22.5877 14.123 22.3966 13.9319 22.3966 13.6971V13.2712H23.2484V14.123H23.2485Z"
                fill="#00A76A"
            />
            <path
                d="M6.7892 16.528C6.36095 16.528 6.01257 16.8763 6.01257 17.3046C6.01257 17.7328 6.36095 18.0812 6.7892 18.0812C7.21745 18.0812 7.56582 17.7328 7.56582 17.3046C7.56582 16.8763 7.21745 16.528 6.7892 16.528Z"
                fill="#00A76A"
            />
            <path
                d="M20.0167 16.528C19.5885 16.528 19.2401 16.8763 19.2401 17.3046C19.2401 17.7328 19.5885 18.0812 20.0167 18.0812C20.445 18.0812 20.7934 17.7328 20.7934 17.3046C20.7934 16.8763 20.445 16.528 20.0167 16.528Z"
                fill="#00A76A"
            />
            <path
                d="M15.6075 14.9247H9.99579C9.78828 14.9247 9.62 15.0929 9.62 15.3005C9.62 15.508 9.78823 15.6763 9.99579 15.6763H15.6075C15.815 15.6763 15.9833 15.508 15.9833 15.3005C15.9833 15.0929 15.815 14.9247 15.6075 14.9247Z"
                fill="#003D4C"
            />
            <path
                d="M5.98746 13.3213H1.17743C0.969917 13.3213 0.801636 13.4895 0.801636 13.6971C0.801636 13.9046 0.96987 14.0729 1.17743 14.0729H5.98746C6.19498 14.0729 6.36326 13.9046 6.36326 13.6971C6.36326 13.4895 6.19498 13.3213 5.98746 13.3213Z"
                fill="#003D4C"
            />
            <path
                d="M13.0674 8.22051C12.9207 8.07379 12.6827 8.07379 12.536 8.22055L9.19416 11.5623L7.45571 9.82387C7.30894 9.6771 7.071 9.6771 6.92428 9.82387C6.77752 9.97063 6.77752 10.2085 6.92428 10.3553L8.92847 12.3595C9.00183 12.4329 9.09802 12.4695 9.19416 12.4695C9.2903 12.4695 9.38653 12.4329 9.45985 12.3595L13.0673 8.75198C13.2141 8.60516 13.2141 8.36727 13.0674 8.22051Z"
                fill="#00A76A"
            />
        </svg>
    );
}

const FreeCopyIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M14.4608 19.125H9.82031C9.5614 19.125 9.35156 18.9152 9.35156 18.6562C9.35156 18.3973 9.5614 18.1875 9.82031 18.1875H14.4608C14.7195 18.1875 14.9295 18.3973 14.9295 18.6562C14.9295 18.9152 14.7195 19.125 14.4608 19.125Z"
            fill="#94EB54"
        />
        <path
            d="M14.4584 15.75H5.60156C5.34283 15.75 5.13281 15.5402 5.13281 15.2812C5.13281 15.0223 5.34283 14.8125 5.60156 14.8125H14.4582C14.7171 14.8125 14.9269 15.0223 14.9269 15.2812C14.9269 15.5402 14.7171 15.75 14.4584 15.75Z"
            fill="#94EB54"
        />
        <path
            d="M14.4584 12.375H5.60156C5.34283 12.375 5.13281 12.1652 5.13281 11.9062C5.13281 11.6473 5.34283 11.4375 5.60156 11.4375H14.4582C14.7171 11.4375 14.9269 11.6473 14.9269 11.9062C14.9269 12.1652 14.7171 12.375 14.4584 12.375Z"
            fill="#94EB54"
        />
        <path
            d="M5.60156 9C5.47815 9 5.3573 8.94983 5.27014 8.86267C5.18298 8.77551 5.13281 8.65448 5.13281 8.53125C5.13281 8.40802 5.18298 8.28699 5.27014 8.19983C5.3573 8.11267 5.47815 8.0625 5.60156 8.0625C5.72479 8.0625 5.84564 8.11267 5.93298 8.19983C6.02014 8.28699 6.07031 8.40802 6.07031 8.53125C6.07031 8.65448 6.02014 8.77551 5.93298 8.86267C5.84564 8.94983 5.72479 9 5.60156 9Z"
            fill="#94EB54"
        />
        <path
            d="M14.4603 9H7.73828C7.47955 9 7.26953 8.79016 7.26953 8.53125C7.26953 8.27234 7.47955 8.0625 7.73828 8.0625H14.4601C14.719 8.0625 14.9288 8.27234 14.9288 8.53125C14.9288 8.79016 14.719 9 14.4603 9Z"
            fill="#94EB54"
        />
        <path
            d="M20.25 6.53125C19.9911 6.53125 19.7812 6.74127 19.7812 7V7.00751C19.7812 7.26642 19.9911 7.47626 20.25 7.47626C20.5089 7.47626 20.7188 7.26642 20.7188 7.00751V7C20.7188 6.74127 20.5089 6.53125 20.25 6.53125Z"
            fill="#94EB54"
        />
        <path
            d="M21.5625 0H7.3125C6.53705 0 5.90625 0.630798 5.90625 1.40625V3.1875H2.90625C2.1308 3.1875 1.5 3.8183 1.5 4.59375V22.5938C1.5 23.3692 2.1308 24 2.90625 24H17.1562C17.9317 24 18.5625 23.3692 18.5625 22.5938V20.8125H21.5625C22.338 20.8125 22.9688 20.1817 22.9688 19.4062V1.40625C22.9688 0.630798 22.338 0 21.5625 0ZM17.625 22.5938C17.625 22.8523 17.4148 23.0625 17.1562 23.0625H2.90625C2.64771 23.0625 2.4375 22.8523 2.4375 22.5938V4.59375C2.4375 4.33521 2.64771 4.125 2.90625 4.125H17.1562C17.4148 4.125 17.625 4.33521 17.625 4.59375V22.5938ZM22.0312 19.4062C22.0312 19.6648 21.821 19.875 21.5625 19.875H18.5625V4.59375C18.5625 3.8183 17.9317 3.1875 17.1562 3.1875H6.84375V1.40625C6.84375 1.14771 7.05396 0.9375 7.3125 0.9375H19.7812V5.15625C19.7812 5.41516 19.9911 5.625 20.25 5.625C20.5089 5.625 20.7188 5.41516 20.7188 5.15625V0.9375H21.5625C21.821 0.9375 22.0312 1.14771 22.0312 1.40625V19.4062Z"
            fill="#00A76A"
        />
    </svg>
);

const AdditionalCopiesIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M14.4608 19.125H9.82031C9.5614 19.125 9.35156 18.9152 9.35156 18.6562C9.35156 18.3973 9.5614 18.1875 9.82031 18.1875H14.4608C14.7195 18.1875 14.9295 18.3973 14.9295 18.6562C14.9295 18.9152 14.7195 19.125 14.4608 19.125Z"
            fill="#003D4C"
        />
        <path
            d="M14.4584 15.75H5.60156C5.34283 15.75 5.13281 15.5402 5.13281 15.2812C5.13281 15.0223 5.34283 14.8125 5.60156 14.8125H14.4582C14.7171 14.8125 14.9269 15.0223 14.9269 15.2812C14.9269 15.5402 14.7171 15.75 14.4584 15.75Z"
            fill="#003D4C"
        />
        <path
            d="M14.4584 12.375H5.60156C5.34283 12.375 5.13281 12.1652 5.13281 11.9062C5.13281 11.6473 5.34283 11.4375 5.60156 11.4375H14.4582C14.7171 11.4375 14.9269 11.6473 14.9269 11.9062C14.9269 12.1652 14.7171 12.375 14.4584 12.375Z"
            fill="#003D4C"
        />
        <path
            d="M5.60156 9C5.47815 9 5.3573 8.94983 5.27014 8.86267C5.18298 8.77551 5.13281 8.65448 5.13281 8.53125C5.13281 8.40802 5.18298 8.28699 5.27014 8.19983C5.3573 8.11267 5.47815 8.0625 5.60156 8.0625C5.72479 8.0625 5.84564 8.11267 5.93298 8.19983C6.02014 8.28699 6.07031 8.40802 6.07031 8.53125C6.07031 8.65448 6.02014 8.77551 5.93298 8.86267C5.84564 8.94983 5.72479 9 5.60156 9Z"
            fill="#003D4C"
        />
        <path
            d="M14.4603 9H7.73828C7.47955 9 7.26953 8.79016 7.26953 8.53125C7.26953 8.27234 7.47955 8.0625 7.73828 8.0625H14.4601C14.719 8.0625 14.9288 8.27234 14.9288 8.53125C14.9288 8.79016 14.719 9 14.4603 9Z"
            fill="#003D4C"
        />
        <path
            d="M20.25 6.53125C19.9911 6.53125 19.7812 6.74127 19.7812 7V7.00751C19.7812 7.26642 19.9911 7.47626 20.25 7.47626C20.5089 7.47626 20.7188 7.26642 20.7188 7.00751V7C20.7188 6.74127 20.5089 6.53125 20.25 6.53125Z"
            fill="#003D4C"
        />
        <path
            d="M21.5625 0H7.3125C6.53705 0 5.90625 0.630798 5.90625 1.40625V3.1875H2.90625C2.1308 3.1875 1.5 3.8183 1.5 4.59375V22.5938C1.5 23.3692 2.1308 24 2.90625 24H17.1562C17.9317 24 18.5625 23.3692 18.5625 22.5938V20.8125H21.5625C22.338 20.8125 22.9688 20.1817 22.9688 19.4062V1.40625C22.9688 0.630798 22.338 0 21.5625 0ZM17.625 22.5938C17.625 22.8523 17.4148 23.0625 17.1562 23.0625H2.90625C2.64771 23.0625 2.4375 22.8523 2.4375 22.5938V4.59375C2.4375 4.33521 2.64771 4.125 2.90625 4.125H17.1562C17.4148 4.125 17.625 4.33521 17.625 4.59375V22.5938ZM22.0312 19.4062C22.0312 19.6648 21.821 19.875 21.5625 19.875H18.5625V4.59375C18.5625 3.8183 17.9317 3.1875 17.1562 3.1875H6.84375V1.40625C6.84375 1.14771 7.05396 0.9375 7.3125 0.9375H19.7812V5.15625C19.7812 5.41516 19.9911 5.625 20.25 5.625C20.5089 5.625 20.7188 5.41516 20.7188 5.15625V0.9375H21.5625C21.821 0.9375 22.0312 1.14771 22.0312 1.40625V19.4062Z"
            fill="#00A76A"
        />
    </svg>
);
